import type { ComponentChildren } from "preact";
import { useId, useRef } from "preact/hooks";
import { Icon } from "./Icon.tsx";
import type { IconName } from "./Icon.tsx";
import { PillGroup } from "./PillGroup.tsx";
import type { PillOption } from "./PillGroup.tsx";
import type { ThemeChoice } from "./lib/theme.ts";

export interface AccountAction {
  label: string; icon?: IconName; href?: string; onSelect?: () => void;
  /** Why it can't be used yet. The action stays in the menu, dimmed, with this note. */
  unavailable?: string;
}

const THEMES: ReadonlyArray<PillOption<ThemeChoice>> = [
  { value: "system", label: "System", title: "Follow this device's setting" },
  { value: "light", label: "Light" },
  { value: "dark", label: "Dark" },
];

const initials = (name: string) => name.split(/\s+/).filter(Boolean).slice(0, 2).map((word) => word[0]?.toUpperCase()).join("");

/**
 * Who's signed in, for AppLayout's `account` slot at the foot of the sidebar.
 * The button opens a menu above it: the account, a theme choice, then actions.
 * Rule: sign out comes last, and an action that isn't connected yet stays
 * visible with `unavailable` saying why rather than disappearing.
 */
export function AccountMenu({ name, email, org, mark, theme, onThemeChange, settings, actions }: {
  name: string; email?: string; org?: string;
  /** Beside the name in the menu, such as a SampleMark while sign-in isn't connected. */
  mark?: ComponentChildren;
  theme?: ThemeChoice; onThemeChange?: (choice: ThemeChoice) => void;
  /** Settings that apply at once, such as Switches, below the theme. */
  settings?: ComponentChildren;
  actions: ReadonlyArray<AccountAction>;
}) {
  const id = useId();
  const menu = useRef<HTMLDivElement>(null);
  const close = () => menu.current?.hidePopover();
  return <div class="ui-account">
    <button type="button" class="ui-account__button" popovertarget={id} aria-label={`Account: ${name}`}>
      <span class="ui-account__avatar" aria-hidden="true">{initials(name)}</span>
      <span class="ui-account__who"><span class="ui-account__name">{name}</span>{email && <span class="ui-account__email">{email}</span>}</span>
      <Icon name="selector" size={16} class="ui-account__chevron" />
    </button>
    <div ref={menu} id={id} popover="auto" class="ui-account__menu">
      <div class="ui-account__head">
        <span class="ui-account__avatar" aria-hidden="true">{initials(name)}</span>
        <div><strong>{name}{mark}</strong>{email && <span>{email}</span>}{org && <span>{org}</span>}</div>
      </div>
      {theme && onThemeChange && <div class="ui-account__theme"><span>Theme</span><PillGroup label="Theme" options={THEMES} value={theme} onChange={onThemeChange} /></div>}
      {settings && <div class="ui-account__settings">{settings}</div>}
      <ul class="ui-account__actions">{actions.map((item) => <li key={item.label}>{accountAction(item, close)}</li>)}</ul>
    </div>
  </div>;
}

function accountAction(item: AccountAction, close: () => void) {
  const body = <>{item.icon && <Icon name={item.icon} size={16} />}<span>{item.label}</span>{item.unavailable && <span class="ui-account__note">{item.unavailable}</span>}</>;
  if (item.unavailable) return <button type="button" class="ui-account__action" aria-disabled="true">{body}</button>;
  if (item.href) return <a class="ui-account__action" href={item.href} onClick={close}>{body}</a>;
  return <button type="button" class="ui-account__action" onClick={() => { close(); item.onSelect?.(); }}>{body}</button>;
}
